import React, { useState, useEffect, useCallback } from 'react';
import { Plus, AlertTriangle, Flag, X } from 'lucide-react';
import { schedulingService } from '../../services/schedulingService';

const DEADLINE_TYPES = [
  { value: 'court_date', label: 'Court Date' },
  { value: 'filing_deadline', label: 'Filing Deadline' },
  { value: 'statute_of_limitations', label: 'Statute of Limitations' },
  { value: 'discovery_cutoff', label: 'Discovery Cutoff' },
  { value: 'response_deadline', label: 'Response Deadline' },
  { value: 'settlement_deadline', label: 'Settlement Deadline' },
  { value: 'appeal_deadline', label: 'Appeal Deadline' },
  { value: 'client_deadline', label: 'Client Deadline' },
  { value: 'regulatory_deadline', label: 'Regulatory Deadline' },
  { value: 'other', label: 'Other' }
];

const DeadlineManager = ({ caseId, onDeadlineChange }) => {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    deadline_date: '',
    deadline_time: '17:00',
    deadline_type: 'filing_deadline',
    is_immovable: true,
    buffer_days: 0,
    reminder_days: [1, 3, 7],
    create_calendar_event: true
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchDeadlines = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await schedulingService.getCaseDeadlines(caseId, true);
      setDeadlines(response.data || []);
    } catch (err) {
      console.error('Error fetching deadlines:', err);
      const serverDetail = err?.response?.data?.detail;
      setError(serverDetail || 'Failed to load deadlines. Please refresh the page to try again.');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchDeadlines();
  }, [fetchDeadlines]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const deadlineDateTime = new Date(`${formData.deadline_date}T${formData.deadline_time}`);

      await schedulingService.createDeadline(caseId, {
        title: formData.title,
        description: formData.description || null,
        deadline_date: deadlineDateTime.toISOString(),
        deadline_type: formData.deadline_type,
        is_immovable: formData.is_immovable,
        buffer_days: parseInt(formData.buffer_days) || 0,
        reminder_days: formData.reminder_days,
        create_calendar_event: formData.create_calendar_event
      });

      // Reset form
      setFormData({
        title: '',
        description: '',
        deadline_date: '',
        deadline_time: '17:00',
        deadline_type: 'filing_deadline',
        is_immovable: true,
        buffer_days: 0,
        reminder_days: [1, 3, 7],
        create_calendar_event: true
      });
      setShowForm(false);

      // Refresh deadlines
      await fetchDeadlines();

      if (onDeadlineChange) {
        onDeadlineChange();
      }
    } catch (err) {
      console.error('Error creating deadline:', err);
      const serverDetail = err?.response?.data?.detail;
      if (serverDetail) {
        setError(serverDetail);
      } else if (err?.message) {
        setError(`Failed to create deadline: ${err.message}`);
      } else {
        setError('Failed to create deadline. Please check your inputs and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getDaysUntil = (deadlineDate) => {
    const now = new Date();
    const deadline = new Date(deadlineDate);
    const diff = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getUrgencyColor = (daysUntil) => {
    if (daysUntil < 0) return 'text-red-700 bg-red-100';
    if (daysUntil <= 3) return 'text-red-600 bg-red-50';
    if (daysUntil <= 7) return 'text-amber-600 bg-amber-50';
    if (daysUntil <= 14) return 'text-yellow-600 bg-yellow-50';
    return 'text-gray-600 bg-gray-50';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const toggleReminderDay = (day) => {
    setFormData(prev => ({
      ...prev,
      reminder_days: prev.reminder_days.includes(day)
        ? prev.reminder_days.filter(d => d !== day)
        : [...prev.reminder_days, day].sort((a, b) => a - b)
    }));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-48"></div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border">
      {/* Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center">
          <Flag className="h-5 w-5 mr-2 text-red-500" />
          Case Deadlines
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          {showForm ? <X className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
          {showForm ? 'Cancel' : 'Add Deadline'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border-b border-red-100">
          <div className="flex items-center text-red-700">
            <AlertTriangle className="h-4 w-4 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="p-4 border-b bg-gray-50 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="e.g., Motion Filing Deadline"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type *
              </label>
              <select
                value={formData.deadline_type}
                onChange={e => setFormData(prev => ({ ...prev, deadline_type: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              >
                {DEADLINE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={formData.deadline_date}
                onChange={e => setFormData(prev => ({ ...prev, deadline_date: e.target.value }))}
                required
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Time
              </label>
              <input
                type="time"
                value={formData.deadline_time}
                onChange={e => setFormData(prev => ({ ...prev, deadline_time: e.target.value }))}
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buffer Days
              </label>
              <input
                type="number"
                value={formData.buffer_days}
                onChange={e => setFormData(prev => ({ ...prev, buffer_days: e.target.value }))}
                min="0"
                max="30"
                className="w-full border rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Additional details about this deadline..."
            />
          </div>

          <div className="flex items-center space-x-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.is_immovable}
                onChange={e => setFormData(prev => ({ ...prev, is_immovable: e.target.checked }))}
                className="rounded border-gray-300 mr-2"
              />
              <span className="text-sm text-gray-700">Immovable deadline</span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.create_calendar_event}
                onChange={e => setFormData(prev => ({ ...prev, create_calendar_event: e.target.checked }))}
                className="rounded border-gray-300 mr-2"
              />
              <span className="text-sm text-gray-700">Create calendar event</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reminder Days Before
            </label>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 5, 7, 14, 30].map(day => (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleReminderDay(day)}
                  className={`px-3 py-1 text-sm rounded border ${
                    formData.reminder_days.includes(day)
                      ? 'bg-blue-100 border-blue-300 text-blue-700'
                      : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {day} {day === 1 ? 'day' : 'days'}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 text-sm border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {submitting ? 'Creating...' : 'Create Deadline'}
            </button>
          </div>
        </form>
      )}

      {/* Deadlines List */}
      <div className="divide-y">
        {deadlines.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Flag className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No deadlines set for this case</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-2 text-blue-600 hover:text-blue-800"
            >
              Add the first deadline
            </button>
          </div>
        ) : (
          deadlines.map(deadline => {
            const daysUntil = getDaysUntil(deadline.deadline_date);
            const urgencyClass = getUrgencyColor(daysUntil);
            const isPast = daysUntil < 0;

            return (
              <div key={deadline.id} className={`p-4 ${isPast ? 'bg-gray-50' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg mr-3 ${urgencyClass}`}>
                      <Flag className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {deadline.title}
                        {deadline.is_immovable && (
                          <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                            Immovable
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {DEADLINE_TYPES.find(t => t.value === deadline.deadline_type)?.label || deadline.deadline_type}
                      </div>
                      {deadline.description && (
                        <div className="text-sm text-gray-500 mt-1">{deadline.description}</div>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="font-medium text-gray-900">
                      {formatDate(deadline.deadline_date)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatTime(deadline.deadline_date)}
                    </div>
                    <div className={`text-sm font-medium mt-1 ${
                      isPast ? 'text-red-600' :
                      daysUntil <= 3 ? 'text-red-600' :
                      daysUntil <= 7 ? 'text-amber-600' :
                      'text-gray-600'
                    }`}>
                      {isPast
                        ? `${Math.abs(daysUntil)} days ago`
                        : daysUntil === 0
                        ? 'Today'
                        : daysUntil === 1
                        ? 'Tomorrow'
                        : `${daysUntil} days`}
                    </div>
                  </div>
                </div>

                {deadline.buffer_days > 0 && (
                  <div className="mt-2 text-xs text-gray-500">
                    Buffer: {deadline.buffer_days} day(s) before deadline
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DeadlineManager;
